import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Menu,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import {
  cancelReceipt,
  deleteReceiptLine,
  getReceipt,
  listReceiptLines,
  listReceivableLines,
  postReceipt,
  updateReceipt,
  type ReceiptLineDto,
  type ReceivableLineDto,
} from './api';
import { ReceiptLineModal } from './ReceiptLineModal';
import { ReceiptStatusBadge } from './ReceiptStatusBadge';

/**
 * One receipt batch: a draft while it is open, a document once it is posted.
 *
 * Two grids, not one. "Still due" is the order's view — what was ordered, what has arrived, what is
 * waiting in other people's open batches — and it is where a line gets started. "On this receipt"
 * is what has been keyed here, and it has more rows than the order does whenever a delivery landed
 * in two places, or arrived as ten serial numbers against one ordered line.
 *
 * Posting is one act for the whole batch, all of it or none of it. Nothing about a posted batch can
 * change afterwards — not a quantity, not a location, not the notes — because the receipt somebody
 * printed and the receipt the system holds have to be the same thing. Undoing one will be a
 * reversal, a new batch pointing back at this one.
 */
export function ReceiptDetailRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const { id } = useParams({ strict: false }) as { id: string };

  const [lineModalOpen, { open: openLineModal, close: closeLineModal }] = useDisclosure(false);
  const [editingLine, setEditingLine] = useState<ReceiptLineDto | null>(null);
  const [receivingLine, setReceivingLine] = useState<ReceivableLineDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canPost = permissions.has('receipts:post');

  const receipt = useQuery({
    queryKey: ['receipt', id] as const,
    queryFn: ({ signal }) => getReceipt(id, signal),
  });

  const lines = useQuery({
    queryKey: ['receipt-lines', id] as const,
    queryFn: ({ signal }) => listReceiptLines(id, signal),
  });

  const batch = receipt.data;

  const receivable = useQuery({
    queryKey: ['receivable-lines', batch?.purchaseOrderId ?? 'none', id] as const,
    queryFn: ({ signal }) => listReceivableLines(batch!.purchaseOrderId!, id, signal),
    enabled: Boolean(batch?.purchaseOrderId),
  });

  // ---- The editable header, held locally until Save ------------------------
  const [packingSlipNumber, setPackingSlipNumber] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!batch) return;
    setPackingSlipNumber(batch.packingSlipNumber ?? '');
    setNotes(batch.notes ?? '');
  }, [batch]);

  const isOpen = batch?.status === 'open';
  const editable = canPost && isOpen;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['receipt', id] });
    void queryClient.invalidateQueries({ queryKey: ['receipt-lines', id] });
    void queryClient.invalidateQueries({ queryKey: ['receivable-lines'] });
    void queryClient.invalidateQueries({ queryKey: ['receipts'] });
    // Posting moves the order's own received quantities, so its screens are stale too.
    void queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
  }

  function describe(err: unknown, fallback: string): string {
    if (!(err instanceof ApiError)) return t(fallback);
    // Every refusal here names what blocked it — which line, how much is already pending, which
    // serial was a duplicate. Restating them generically drops the only actionable part.
    return err.problem.detail ?? err.problem.title ?? t(fallback);
  }

  const saveHeader = useMutation({
    mutationFn: () =>
      updateReceipt(id, {
        packingSlipNumber: packingSlipNumber.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      notifications.show({ message: t('receipts.saved'), color: 'green' });
      setActionError(null);
      refresh();
    },
    onError: (err) => setActionError(describe(err, 'receipts.errors.saveFailed')),
  });

  const post = useMutation({
    mutationFn: () => postReceipt(id),
    onSuccess: (posted) => {
      notifications.show({
        message: t('receipts.postedNotice', {
          batch: posted.batchNumber,
          quantity: posted.totalQuantity.toLocaleString(),
        }),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      setActionError(null);
      refresh();
    },
    onError: (err) => setActionError(describe(err, 'receipts.errors.postFailed')),
  });

  const cancel = useMutation({
    mutationFn: () => cancelReceipt(id),
    onSuccess: () => {
      notifications.show({ message: t('receipts.cancelledNotice'), color: 'green' });
      setActionError(null);
      refresh();
    },
    onError: (err) => setActionError(describe(err, 'receipts.errors.cancelFailed')),
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => deleteReceiptLine(id, lineId),
    onSuccess: () => {
      setActionError(null);
      refresh();
    },
    onError: (err) => setActionError(describe(err, 'receipts.errors.lineFailed')),
  });

  /**
   * TWO GRIDS, TWO SHAPES.
   *
   * The still-due grid is the ORDER's view, so the item is its headline and the balance is the
   * aside: "how much of this is left" is the number somebody reads before deciding to key a line.
   * "At another door" stays a labelled row of its own — folding it into arrived is exactly how two
   * doors book one delivery twice.
   *
   * The keyed grid is a list of what was put down, so the item is the headline and the quantity the
   * aside, with location, unit and lot/serial as labelled rows underneath.
   */
  const receivableColumns: DataColumn<ReceivableLineDto>[] = useMemo(
    () => [
      {
        key: 'item',
        label: t('receipts.item'),
        card: 'title',
        render: (r) => (
          <Group gap={6} wrap="nowrap">
            <div>
              <Text size="sm">{r.itemNumber}</Text>
              <Text size="xs" c="dimmed">
                {r.itemDescription}
              </Text>
            </div>
            {/* The flags that change what a line asks for, visible before it is opened — a
                serialized line means one row per serial number. */}
            {r.isLotControlled && (
              <Badge size="xs" variant="light" color="grape">
                {t('receipts.lotBadge')}
              </Badge>
            )}
            {r.isSerialized && (
              <Badge size="xs" variant="light" color="teal">
                {t('receipts.serialBadge')}
              </Badge>
            )}
          </Group>
        ),
      },
      {
        key: 'lineNumber',
        label: t('receipts.lineNumber'),
        width: 60,
        card: 'hidden',
        render: (r) => r.lineNumber,
      },
      {
        key: 'balanceDue',
        label: t('receipts.balanceDue'),
        align: 'right',
        card: 'aside',
        render: (r) => (
          <Text size="sm" fw={500}>
            {r.balanceDue.toLocaleString()} {r.uomCode}
          </Text>
        ),
      },
      {
        key: 'ordered',
        label: t('receipts.ordered'),
        align: 'right',
        render: (r) => r.quantityOrdered.toLocaleString(),
      },
      {
        key: 'arrived',
        label: t('receipts.arrived'),
        align: 'right',
        render: (r) => r.quantityReceived.toLocaleString(),
      },
      {
        key: 'pendingElsewhere',
        label: t('receipts.pendingElsewhere'),
        align: 'right',
        // The term that stops one delivery being booked twice at two doors.
        render: (r) =>
          r.pendingInOtherBatches > 0 ? (
            <Tooltip label={t('receipts.pendingElsewhereHint')}>
              <Text size="sm" c="orange">
                {r.pendingInOtherBatches.toLocaleString()}
              </Text>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        key: 'onThisReceipt',
        label: t('receipts.onThisReceipt'),
        align: 'right',
        render: (r) => (r.pendingInThisBatch > 0 ? r.pendingInThisBatch.toLocaleString() : '—'),
      },
      {
        key: 'unit',
        label: t('receipts.unit'),
        card: 'hidden',
        render: (r) => r.uomCode,
      },
    ],
    [t],
  );

  const lineColumns: DataColumn<ReceiptLineDto>[] = useMemo(
    () => [
      {
        key: 'item',
        label: t('receipts.item'),
        card: 'title',
        render: (l) => (
          <>
            <Text size="sm">{l.itemNumber}</Text>
            <Text size="xs" c="dimmed">
              {l.itemDescription}
            </Text>
          </>
        ),
      },
      {
        key: 'lineNumber',
        label: t('receipts.lineNumber'),
        width: 60,
        card: 'hidden',
        render: (l) => l.lineNumber,
      },
      {
        key: 'quantity',
        label: t('receipts.quantityReceived'),
        align: 'right',
        card: 'aside',
        render: (l) => (
          <Text size="sm" fw={500}>
            {l.quantityReceived.toLocaleString()} {l.uomCode}
          </Text>
        ),
      },
      {
        key: 'unit',
        label: t('receipts.unit'),
        card: 'hidden',
        render: (l) => l.uomCode,
      },
      {
        key: 'location',
        label: t('receipts.location'),
        render: (l) => (
          <>
            <Text size="sm">{l.locationCode}</Text>
            {l.licensePlateNumber && (
              <Text size="xs" c="dimmed">
                {l.licensePlateNumber}
              </Text>
            )}
          </>
        ),
      },
      {
        key: 'lotSerial',
        label: t('receipts.lotSerial'),
        render: (l) => (
          <>
            {l.lotNumber && <Text size="sm">{l.lotNumber}</Text>}
            {l.serialNumber && (
              <Text size="xs" c="dimmed">
                {l.serialNumber}
              </Text>
            )}
            {!l.lotNumber && !l.serialNumber && '—'}
          </>
        ),
      },
    ],
    [t],
  );

  if (receipt.isLoading) {
    return (
      <>
        <PageHeader title={t('nav.receiving')} back />
        <Center py="xl">
          <Loader color="invetrak" />
        </Center>
      </>
    );
  }

  if (receipt.isError || !batch) {
    return (
      <>
        <PageHeader title={t('nav.receiving')} back />
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('receipts.errors.notFound')}
        </Alert>
      </>
    );
  }

  const lineRows = lines.data ?? [];
  const canPostNow = isOpen && lineRows.length > 0;

  function startLine(r: ReceivableLineDto) {
    setEditingLine(null);
    setReceivingLine(r);
    openLineModal();
  }

  function editLine(l: ReceiptLineDto) {
    setEditingLine(l);
    // The flags that decide which fields appear live on the order line, so an edit needs the same
    // receivable row a new line would have used.
    setReceivingLine(
      (receivable.data ?? []).find((r) => r.purchaseOrderLineId === l.purchaseOrderLineId) ?? null,
    );
    openLineModal();
  }

  return (
    <>
      <PageHeader
        title={batch.batchNumber}
        back
        subtitle={
          <Group gap="xs">
            <ReceiptStatusBadge status={batch.status} />
            <Text size="sm" c="dimmed">
              {t('receipts.headline', {
                supplier: batch.supplierName ?? '—',
                po: batch.poNumber ?? '—',
                warehouse: batch.warehouseCode,
              })}
            </Text>
          </Group>
        }
        actions={
          canPost && (
            <Group gap="sm">
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button variant="default" rightSection={<IconChevronDown size={16} />}>
                    {t('receipts.actions')}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {/* Both mirror the sprocs exactly: only an open batch with lines can be posted
                      (53829/53831), and a posted one can never be cancelled (53830). */}
                  <Menu.Item disabled={!canPostNow} onClick={() => post.mutate()}>
                    {t('receipts.post')}
                  </Menu.Item>
                  <Menu.Item color="red" disabled={!isOpen} onClick={() => cancel.mutate()}>
                    {t('receipts.cancel')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Button
                color="invetrak"
                onClick={() => saveHeader.mutate()}
                loading={saveHeader.isPending}
                disabled={!editable}
              >
                {t('shell.saveChanges')}
              </Button>
            </Group>
          )
        }
      />

      <Stack gap="md">
        {batch.status === 'posted' && (
          <Alert color="green" variant="light" icon={<IconCheck size={16} />}>
            {t('receipts.postedNotice2', {
              when: batch.postedAtUtc ? new Date(batch.postedAtUtc).toLocaleString() : '',
            })}
          </Alert>
        )}

        {batch.status === 'cancelled' && (
          <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('receipts.cancelledNotice2')}
          </Alert>
        )}

        {actionError && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {actionError}
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>{t('receipts.deliverySection')}</Title>

              {/* Fixed for the life of the batch — shown, not offered. */}
              <SimpleGrid cols={2} spacing="xs">
                <Text size="sm" c="dimmed">{t('receipts.purchaseOrder')}</Text>
                <Text size="sm">{batch.poNumber ?? '—'}</Text>
                <Text size="sm" c="dimmed">{t('receipts.supplier')}</Text>
                <Text size="sm">
                  {batch.supplierNumber ? `${batch.supplierNumber} — ${batch.supplierName}` : '—'}
                </Text>
                <Text size="sm" c="dimmed">{t('receipts.warehouse')}</Text>
                <Text size="sm">{batch.warehouseCode}</Text>
                <Text size="sm" c="dimmed">{t('receipts.started')}</Text>
                <Text size="sm">{new Date(batch.createdAtUtc).toLocaleString()}</Text>
              </SimpleGrid>

              <Divider />

              <TextInput
                label={t('receipts.packingSlip')}
                description={t('receipts.packingSlipHint')}
                value={packingSlipNumber}
                onChange={(e) => setPackingSlipNumber(e.currentTarget.value)}
                maxLength={50}
                disabled={!editable}
              />
              <Textarea
                label={t('receipts.notes')}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                autosize
                minRows={2}
                maxRows={5}
                disabled={!editable}
              />
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>{t('receipts.shipToSection')}</Title>

              {/* Taken at POST time, from the order. Before that there is nothing to show, and
                  reading the order live would let a later correction rewrite where the goods
                  actually went. */}
              {batch.shipToName ? (
                <Stack gap={2}>
                  <Text size="sm" fw={500}>{batch.shipToName}</Text>
                  {batch.shipToAddressLine1 && <Text size="sm">{batch.shipToAddressLine1}</Text>}
                  {batch.shipToAddressLine2 && <Text size="sm">{batch.shipToAddressLine2}</Text>}
                  <Text size="sm">
                    {[batch.shipToCity, batch.shipToStateProvince, batch.shipToPostalCode]
                      .filter(Boolean)
                      .join(', ')}
                  </Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    {t('receipts.shipToSnapshot')}
                  </Text>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">{t('receipts.shipToPending')}</Text>
              )}

              <Divider />

              <SimpleGrid cols={2} spacing="xs">
                <Text size="sm" c="dimmed">{t('receipts.lines')}</Text>
                <Text size="sm" ta="right">{batch.lineCount}</Text>
                <Text fw={600}>{t('receipts.totalQuantity')}</Text>
                <Text fw={600} ta="right">{batch.totalQuantity.toLocaleString()}</Text>
              </SimpleGrid>
              <Text size="xs" c="dimmed">{t('receipts.totalMixedUnits')}</Text>
            </Stack>
          </Card>
        </SimpleGrid>

        {/* ---- What the order still owes ------------------------------------ */}
        <Card withBorder>
          <Title order={4} mb="md">{t('receipts.stillDueSection')}</Title>

          {receivable.isLoading ? (
            <Center py="lg">
              <Loader color="invetrak" size="sm" />
            </Center>
          ) : (receivable.data ?? []).length === 0 ? (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('receipts.nothingDue')}
            </Alert>
          ) : (
            <ResponsiveTable
              rows={receivable.data ?? []}
              columns={receivableColumns}
              rowKey={(r) => r.purchaseOrderLineId}
              minWidth={900}
              actionsWidth={110}
              striped
              actions={
                editable
                  ? (r) => (
                      <Button
                        size="compact-sm"
                        variant="light"
                        color="invetrak"
                        onClick={() => startLine(r)}
                      >
                        {t('receipts.receive')}
                      </Button>
                    )
                  : undefined
              }
            />
          )}
        </Card>

        {/* ---- What has been keyed onto this receipt ------------------------- */}
        <Card withBorder>
          <Title order={4} mb="md">{t('receipts.linesSection')}</Title>

          {lines.isLoading ? (
            <Center py="lg">
              <Loader color="invetrak" size="sm" />
            </Center>
          ) : lineRows.length === 0 ? (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('receipts.noLinesYet')}
            </Alert>
          ) : (
            <ResponsiveTable
              rows={lineRows}
              columns={lineColumns}
              rowKey={(l) => l.receiptBatchLineId}
              minWidth={900}
              striped
              actions={
                editable
                  ? (l) => (
                      <>
                        <Tooltip label={t('receipts.editLineAria', { number: l.lineNumber })}>
                          <ActionIcon
                            variant="subtle"
                            aria-label={t('receipts.editLineAria', { number: l.lineNumber })}
                            onClick={() => editLine(l)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('receipts.deleteLineAria', { number: l.lineNumber })}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={t('receipts.deleteLineAria', { number: l.lineNumber })}
                            onClick={() => removeLine.mutate(l.receiptBatchLineId)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )
                  : undefined
              }
            />
          )}
        </Card>

        <Group>
          <Button variant="default" onClick={() => void navigate({ to: '/app/receiving' })}>
            {t('receipts.done')}
          </Button>
        </Group>
      </Stack>

      <ReceiptLineModal
        receiptBatchId={id}
        warehouseId={batch.warehouseId}
        receivable={receivingLine}
        line={editingLine}
        opened={lineModalOpen}
        onClose={closeLineModal}
        onSaved={() => refresh()}
      />
    </>
  );
}
